import React, { useState, useEffect, useCallback, Suspense } from 'react';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DirectoryHistoryTabs } from '../components/directory/DirectoryHistoryTabs';
import { toast } from 'sonner';
import { Users, Search, Loader2, Globe } from 'lucide-react';
const CommentSection = React.lazy(() => import('../components/CommentSection'));

const PAGE_SIZE = 50;

export default function DirectoryPage() {
  const { isAdmin, canAccess } = useAuth();
  const [tab, setTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendorTotal, setVendorTotal] = useState(0);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [vendorOwners, setVendorOwners] = useState([]);
  const [customerOwners, setCustomerOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState(null);
  const [dData, setDData] = useState(null);
  const [dLoading, setDLoading] = useState(false);
  const [countryEdit, setCountryEdit] = useState('');
  const [countrySaving, setCountrySaving] = useState(false);
  const [countrySync, setCountrySync] = useState(null);

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (ownerFilter && ownerFilter !== 'all') params.owner = ownerFilter;
      const r = await api.get('/zoho/vendors', { params });
      setVendors(r.data.vendors || []);
      setVendorTotal(r.data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        setVendors([]);
        setVendorTotal(0);
      } else {
        toast.error('Failed to load vendors');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, ownerFilter]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (ownerFilter && ownerFilter !== 'all') params.owner = ownerFilter;
      const r = await api.get('/zoho/customers', { params });
      setCustomers(r.data.customers || []);
      setCustomerTotal(r.data.total || 0);
    } catch (e) {
      if (e.response?.status === 403) {
        setCustomers([]);
        setCustomerTotal(0);
      } else {
        toast.error('Failed to load customers');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, ownerFilter]);

  useEffect(() => {
    // Fetch both totals and owner lists on mount
    api.get('/zoho/vendors', { params: { limit: 1 } }).then(r => setVendorTotal(r.data.total || 0)).catch(() => {});
    api.get('/zoho/customers', { params: { limit: 1 } }).then(r => setCustomerTotal(r.data.total || 0)).catch(() => {});
    api.get('/zoho/vendor-owners').then(r => setVendorOwners(r.data.owners || [])).catch(() => {});
    api.get('/zoho/customer-owners').then(r => setCustomerOwners(r.data.owners || [])).catch(() => {});
    if (isAdmin) refreshCountrySyncStatus();
  }, []);

  const refreshCountrySyncStatus = () => {
    api.get('/zoho/vendors/sync-countries/status').then(r => setCountrySync(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!countrySync?.running) return;
    const t = setInterval(refreshCountrySyncStatus, 4000);
    return () => clearInterval(t);
  }, [countrySync?.running]);

  const startCountrySync = async () => {
    try {
      const r = await api.post('/zoho/vendors/sync-countries');
      if (r.data.status === 'already_running') toast.info('Country sync is already running');
      else toast.info('Started pulling vendor countries from Zoho — this can take a while for large vendor lists');
      refreshCountrySyncStatus();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start sync'); }
  };

  useEffect(() => {
    setPage(0);
    setOwnerFilter('all');
  }, [tab]);

  useEffect(() => {
    setPage(0);
  }, [search, ownerFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (tab === 'vendors') fetchVendors();
      else fetchCustomers();
    }, 300);
    return () => clearTimeout(t);
  }, [tab, page, search, fetchVendors, fetchCustomers]);

  const openDetail = async (item, type) => {
    const name = type === 'vendor' ? item.vendor_name : item.customer_name;
    setDetail({ name, type, raw: item }); setDData(null); setDLoading(true);
    setCountryEdit(item.country || '');
    const url = type === 'vendor' ? `/zoho/vendors/${encodeURIComponent(name)}/history` : `/zoho/customers/${encodeURIComponent(name)}/history`;
    try { setDData((await api.get(url)).data); } catch (e) { toast.error('Failed to load details'); } finally { setDLoading(false); }
  };

  const saveVendorCountry = async () => {
    if (!detail?.raw?.zoho_vendor_id) return;
    setCountrySaving(true);
    try {
      await api.put(`/zoho/vendors/${detail.raw.zoho_vendor_id}/country`, { country: countryEdit.trim() });
      toast.success('Vendor country updated');
      setVendors(prev => prev.map(v => v.zoho_vendor_id === detail.raw.zoho_vendor_id ? { ...v, country: countryEdit.trim() } : v));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update country'); } finally { setCountrySaving(false); }
  };

  const list = tab === 'vendors' ? vendors : customers;
  const total = tab === 'vendors' ? vendorTotal : customerTotal;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const stats = Object.entries(dData?.stats || {}).slice(0, 4);

  return (
    <div className="space-y-4" data-testid="directory-page">
      <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" /> Vendors & Customers</h1>
      <div className="flex gap-2 items-center flex-wrap">
        <Button variant={tab === 'vendors' ? 'default' : 'outline'} size="sm" onClick={() => { setTab('vendors'); setSearch(''); }} data-testid="vendors-dir-tab">Vendors ({vendorTotal})</Button>
        <Button variant={tab === 'customers' ? 'default' : 'outline'} size="sm" onClick={() => { setTab('customers'); setSearch(''); }} data-testid="customers-dir-tab">Customers ({customerTotal})</Button>
        <Select value={ownerFilter} onValueChange={setOwnerFilter}>
          <SelectTrigger className="w-48 h-9 rounded-sm" data-testid="owner-filter">
            <SelectValue placeholder="Filter by owner" />
          </SelectTrigger>
          <SelectContent>
            {isAdmin && <SelectItem value="all">All Owners</SelectItem>}
            {(tab === 'vendors' ? vendorOwners : customerOwners).map(o => (
              <SelectItem key={o.name} value={o.name}>{o.name} ({o.count})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isAdmin && tab === 'vendors' && (
          <Button variant="outline" size="sm" className="rounded-sm" onClick={startCountrySync} disabled={countrySync?.running} data-testid="sync-vendor-countries-btn">
            {countrySync?.running ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Globe className="h-3.5 w-3.5 mr-1.5" />}
            {countrySync?.running ? `Syncing ${countrySync.completed}/${countrySync.total}...` : 'Sync Countries from Zoho'}
          </Button>
        )}
        <div className="relative ml-auto"><Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" /><Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="pl-8 w-60 h-9" data-testid="dir-search" /></div>
      </div>
      {isAdmin && tab === 'vendors' && countrySync && !countrySync.running && countrySync.total > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="country-sync-summary">Last sync: found a country for {countrySync.found} of {countrySync.total} vendors that were missing one.</p>
      )}

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" /></div>
      ) : (
        <>
          <Card className="rounded-sm"><CardContent className="p-0">
            {list.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No {tab} found</div>
            ) : (
              <>
                {/* Header row */}
                <div className="flex items-center px-3 py-2 border-b bg-muted/50 text-xs font-medium uppercase text-muted-foreground">
                  <span className="flex-1">Name</span>
                  <span className="w-40 text-center">Assigned To</span>
                  <span className="w-20 text-right">Currency</span>
                </div>
                {list.map((item, i) => {
              const name = tab === 'vendors' ? item.vendor_name : item.customer_name;
              const assignedTo = tab === 'vendors'
                ? (item.assigned_purchase_user_name || item.crm_owner_name || '')
                : (item.assigned_sales_user_name || item.crm_owner_name || '');
              return (
                <div key={i} className="flex items-center justify-between p-3 border-b hover:bg-muted/30 cursor-pointer" onClick={() => openDetail(item, tab === 'vendors' ? 'vendor' : 'customer')} data-testid={`dir-row-${i}`}>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-sm truncate block max-w-[400px]">{name}</span>
                  </div>
                  <span className="w-40 text-center text-sm">
                    {assignedTo ? (
                      <Badge variant="outline" className="text-xs rounded-sm">{assignedTo}</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unassigned</span>
                    )}
                  </span>
                  <span className="w-20 text-right text-xs text-muted-foreground">
                    {tab === 'vendors' && <Badge variant="outline" className="text-xs">{item.currency_code || 'USD'}</Badge>}
                    {tab === 'customers' && item.total_orders && `${item.total_orders} orders`}
                  </span>
                </div>
              );
            })}
              </>
            )}
          </CardContent></Card>

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total} {tab}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm">Previous</Button>
                <span className="flex items-center px-3 text-sm">Page {page + 1} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="rounded-sm">Next</Button>
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="directory-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detail?.name}</DialogTitle>
            <DialogDescription>{detail?.type === 'vendor' ? 'Vendor purchase history and notes' : 'Customer sales history and notes'}</DialogDescription>
          </DialogHeader>
          {isAdmin && detail?.type === 'vendor' && (
            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-sm" data-testid="vendor-country-edit">
              <span className="text-xs font-medium text-muted-foreground shrink-0">Country (for Bid History breakdown):</span>
              <Input value={countryEdit} onChange={e => setCountryEdit(e.target.value)} placeholder="e.g. United States" className="h-8 rounded-sm text-sm" data-testid="vendor-country-input" />
              <Button size="sm" onClick={saveVendorCountry} disabled={countrySaving} className="rounded-sm h-8 shrink-0" data-testid="vendor-country-save-btn">
                {countrySaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
              </Button>
            </div>
          )}
          {dLoading && <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}
          {!dLoading && dData && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {stats.map(([k, v]) => <div key={k} className="p-2 bg-muted rounded text-center"><p className="text-xs text-muted-foreground">{k.replace(/_/g, ' ')}</p><p className="font-bold">{typeof v === 'number' && v > 1000 ? formatCurrency(v) : v}</p></div>)}
              </div>
              <DirectoryHistoryTabs type={detail?.type} data={dData}
                meetingsCompanyType={canAccess('MEETINGS', 'VIEW') ? (detail?.type === 'vendor' ? 'VENDOR' : 'CUSTOMER') : null}
                meetingsCompanyRef={detail?.type === 'vendor' ? detail?.raw?.zoho_vendor_id : detail?.name} />
              <Suspense fallback={<div className="py-4 text-center text-muted-foreground text-sm">Loading comments...</div>}>
                <CommentSection entityType={detail?.type} entityId={detail?.name} title={`${detail?.type === 'vendor' ? 'Vendor' : 'Customer'} Notes`} />
              </Suspense>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
